import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  EnvelopeSimple, WhatsappLogo, CalendarBlank, MapPin, ShieldCheck, FloppyDisk, Info,
} from "@phosphor-icons/react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Sliders } from "@phosphor-icons/react";
import { useTranslation, Trans } from 'react-i18next'

const INTEGRATIONS = [
  { key: "gmail", label: "Gmail / Professional Email", icon: EnvelopeSimple, fields: ["gmail_client_id", "gmail_client_secret"], help: "Connect via Google OAuth to send emails from your account." },
  { key: "whatsapp", label: "WhatsApp Business API", icon: WhatsappLogo, fields: ["whatsapp_token", "whatsapp_phone_id"], help: "Requires a Meta WhatsApp Business account and access token." },
  { key: "google_calendar", label: "Google Calendar", icon: CalendarBlank, fields: ["gcal_client_id", "gcal_client_secret"], help: "Sync visits and follow-ups with Google Calendar." },
];

export default function Settings() {
  const { t } = useTranslation()
  const qc = useQueryClient();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const { data: settings } = useQuery({ queryKey: ["settings"], queryFn: async () => (await api.get("/settings")).data });
  const [form, setForm] = useState(null);
  const [secrets, setSecrets] = useState({});

  const { data: securitySettings } = useQuery({
    queryKey: ["security-alert-settings"],
    queryFn: async () => (await api.get("/settings/security-alerts")).data,
    enabled: isAdmin,
  });
  const [securityForm, setSecurityForm] = useState(null);

  useEffect(() => {
    if (securitySettings) setSecurityForm(securitySettings);
  }, [securitySettings]);

  const saveSecurity = useMutation({
    mutationFn: async (payload) => (await api.put("/settings/security-alerts", payload)).data,
    onSuccess: (data) => {
      setSecurityForm(data);
      qc.invalidateQueries({ queryKey: ["security-alert-settings"] });
      toast.success(t("securityAlerts.saved", "Security alert settings saved"));
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t("securityAlerts.saveFailed", "Could not save security alert settings"));
    },
  });

  const testSecurity = useMutation({
    mutationFn: async () => (await api.post("/settings/security-alerts/test")).data,
    onSuccess: (data) => {
      if (data?.dry_run) {
        toast.success(t("securityAlerts.testDryRun", "Test alert created successfully in dry-run mode"));
      } else {
        toast.success(t("securityAlerts.testSent", "Test alert processed"));
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t("securityAlerts.testFailed", "Test alert failed"));
    },
  });

  const testEmail = useMutation({
    mutationFn: async () => (await api.post("/settings/security-alerts/test-email")).data,
    onSuccess: (data) => {
      qc.invalidateQueries({ queryKey: ["security-alert-recent"] });
      if (data?.email_status === "sent") {
        toast.success(t("securityAlerts.testEmailSent", "Test email sent successfully"));
      } else if (data?.email_status === "dry_run" || data?.dry_run) {
        toast.success(t("securityAlerts.testEmailDryRun", "Test email processed in dry-run mode (no email sent)"));
      } else {
        toast.error(t("securityAlerts.testEmailFailed", "Test email could not be sent"));
      }
    },
    onError: (err) => {
      toast.error(err?.response?.data?.detail || t("securityAlerts.testEmailFailed", "Test email could not be sent"));
    },
  });

  const { data: recentAlerts } = useQuery({
    queryKey: ["security-alert-recent"],
    queryFn: async () => (await api.get("/settings/security-alerts/recent")).data,
    enabled: isAdmin,
  });

  const { data: scoring } = useQuery({ queryKey: ["scoring-weights"], queryFn: async () => (await api.get("/scoring/weights")).data });
  const [weights, setWeights] = useState(null);
  useEffect(() => { if (scoring && !weights) setWeights(scoring.weights); }, [scoring, weights]);

  const saveWeights = useMutation({
    mutationFn: async (payload) => (await api.put("/scoring/weights", { weights: payload })).data,
    onSuccess: (d) => { qc.invalidateQueries({ queryKey: ["scoring-weights"] }); qc.invalidateQueries({ queryKey: ["scoring-contacts"] }); qc.invalidateQueries({ queryKey: ["scoring-opportunities"] }); qc.invalidateQueries({ queryKey: ["priority-opps"] }); setWeights(d.weights); toast.success("Scoring weights saved"); },
    onError: () => toast.error("Only admins can change scoring weights"),
  });

  useEffect(() => { if (settings && !form) setForm(settings); }, [settings, form]);

  const save = useMutation({
    mutationFn: async (payload) => (await api.put("/settings", payload)).data,
    onSuccess: () => { qc.invalidateQueries({ queryKey: ["settings"] }); toast.success("Settings saved"); },
    onError: () => toast.error("Only admins can change settings"),
  });

  if (!form) return <div className="flex justify-center py-40"><div className="animate-spin h-8 w-8 border-2 border-[#0B2545] border-t-transparent rounded-full" /></div>;

  const set = (k, v) => setForm({ ...form, [k]: v });
  const setInt = (k, v) => setForm({ ...form, integrations: { ...form.integrations, [k]: v } });

  const doSave = () => {
    const payload = { ...form };
    if (Object.keys(secrets).length) payload.secrets = secrets;
    save.mutate(payload);
    setSecrets({});
  };

  return (
    <div>
      <PageHeader
        title="Settings"
        subtitle="Company profile and integration configuration"
        actions={isAdmin && <Button data-testid="save-settings" onClick={doSave} className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white"><FloppyDisk size={16} className="mr-1.5" /> {t('save', 'Save')}</Button>}
      />

      {!isAdmin && (
        <div className="bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 mb-6 text-sm text-amber-800 flex items-center gap-2">
          <Info size={18} /> {t('onlyAdministratorsCanModifySettings', 'Only administrators can modify settings.')}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <h3 className="font-serif text-xl">{t('companyConsultant', 'Company & Consultant')}</h3>
          <div><Label>{t('companyName', 'Company Name')}</Label><Input data-testid="company-name" disabled={!isAdmin} value={form.company_name || ""} onChange={(e) => set("company_name", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('consultantName', 'Consultant Name')}</Label><Input disabled={!isAdmin} value={form.consultant_name || ""} onChange={(e) => set("consultant_name", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('consultantEmail', 'Consultant Email')}</Label><Input disabled={!isAdmin} value={form.consultant_email || ""} onChange={(e) => set("consultant_email", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('consultantPhone', 'Consultant Phone')}</Label><Input disabled={!isAdmin} value={form.consultant_phone || ""} onChange={(e) => set("consultant_phone", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div className="border-t border-slate-200 pt-4">
            <h4 className="font-medium text-[#0B2545]">
              {t('legalCompanyIdentity', 'Verified legal identity')}
            </h4>
            <p className="mt-1 text-xs text-slate-500">
              {t('legalCompanyIdentityHelp', 'Used in controlled legal documents. Enter only verified registration information.')}
            </p>
          </div>
          <div><Label>{t('legalCompanyName', 'Registered legal name')}</Label><Input disabled={!isAdmin} value={form.legal_company_name || ""} onChange={(e) => set("legal_company_name", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('tradingName', 'Trading name')}</Label><Input disabled={!isAdmin} value={form.trading_name || ""} onChange={(e) => set("trading_name", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('companyRegistrationNumber', 'Company registration number')}</Label><Input disabled={!isAdmin} value={form.company_registration_number || ""} onChange={(e) => set("company_registration_number", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('taxNumber', 'Tax number')}</Label><Input disabled={!isAdmin} value={form.tax_number || ""} onChange={(e) => set("tax_number", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('impicLicenseNumber', 'IMPIC licence number')}</Label><Input disabled={!isAdmin} value={form.impic_license_number || ""} onChange={(e) => set("impic_license_number", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('registeredAddress', 'Registered office')}</Label><Input disabled={!isAdmin} value={form.registered_address || ""} onChange={(e) => set("registered_address", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('legalEmail', 'Legal email')}</Label><Input type="email" disabled={!isAdmin} value={form.legal_email || ""} onChange={(e) => set("legal_email", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('legalPhone', 'Legal telephone')}</Label><Input disabled={!isAdmin} value={form.legal_phone || ""} onChange={(e) => set("legal_phone", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('website', 'Website')}</Label><Input disabled={!isAdmin} value={form.website || ""} onChange={(e) => set("website", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div><Label>{t('defaultGoverningLaw', 'Default governing law')}</Label><Input disabled={!isAdmin} value={form.default_governing_law || ""} onChange={(e) => set("default_governing_law", e.target.value)} className="mt-1 rounded-sm" /></div>
          <div>
            <Label>{t('defaultControllingLanguage', 'Default controlling language')}</Label>
            <select
              disabled={!isAdmin}
              value={form.default_controlling_language || "pt"}
              onChange={(e) => set("default_controlling_language", e.target.value)}
              className="mt-1 h-10 w-full rounded-sm border border-slate-200 bg-white px-3 text-sm disabled:bg-slate-50"
            >
              <option value="en">English</option>
              <option value="pt">Português</option>
              <option value="zh">中文</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
              <option value="de">Deutsch</option>
              <option value="it">Italiano</option>
              <option value="ru">Русский</option>
            </select>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-sm p-6 space-y-4">
          <h3 className="font-serif text-xl flex items-center gap-2"><MapPin size={20} className="text-[#C5A059]" /> {t('maps', 'Maps')}</h3>
          <p className="text-sm text-slate-500"><Trans i18nKey="interactiveMapsUseStrongopenstreetmapstrongNoApiKeyNeededToUseGoogleMapsInsteadAddAKeyBelow">Interactive maps use <strong>OpenStreetMap</strong> (no API key needed). To use Google Maps instead, add a key below.</Trans></p>
          <div><Label>{t('googleMapsApiKeyOptional', 'Google Maps API Key (optional)')}</Label><Input disabled={!isAdmin} type="password" placeholder={settings?.secrets_configured?.google_maps_key ? "•••••••• configured" : t('notConfigured', 'Not configured')} onChange={(e) => setSecrets({ ...secrets, google_maps_key: e.target.value })} className="mt-1 rounded-sm" /></div>
          <div className="pt-2 flex items-center gap-2 text-sm text-emerald-700"><ShieldCheck size={18} /> {t('credentialsAreStoredSecurelyOnTheServerNeverExposedToTheBrowser', 'Credentials are stored securely on the server, never exposed to the browser.')}</div>
        </div>
      </div>

      <div className="mt-6 space-y-4">
        <h3 className="font-serif text-xl">{t('integrations', 'Integrations')}</h3>
        {INTEGRATIONS.map((it) => (
          <div key={it.key} className="bg-white border border-slate-200 rounded-sm p-6" data-testid={`integration-${it.key}`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <span className="w-10 h-10 rounded-sm bg-[#0B2545]/10 flex items-center justify-center"><it.icon size={20} className="text-[#0B2545]" /></span>
                <div>
                  <div className="font-medium text-slate-900">{it.label}</div>
                  <div className="text-xs text-slate-500">{it.help}</div>
                </div>
              </div>
              <label className="flex items-center gap-2 text-sm">
                <Switch disabled={!isAdmin} checked={!!form.integrations?.[it.key]} onCheckedChange={(v) => setInt(it.key, v)} data-testid={`toggle-${it.key}`} />
                <span className="text-slate-500">{form.integrations?.[it.key] ? "Enabled" : "Disabled"}</span>
              </label>
            </div>
            {form.integrations?.[it.key] && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                {it.fields.map((f) => (
                  <div key={f}>
                    <Label className="capitalize">{f.replace(/_/g, " ")}</Label>
                    <Input disabled={!isAdmin} type="password" placeholder={settings?.secrets_configured?.[f] ? "•••••••• configured" : "Enter credential"} onChange={(e) => setSecrets({ ...secrets, [f]: e.target.value })} className="mt-1 rounded-sm" />
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
        <div className="bg-blue-50 border border-blue-200 rounded-sm px-4 py-3 text-sm text-blue-800 flex items-start gap-2">
          <Info size={18} className="flex-shrink-0 mt-0.5" />
          <span><Trans i18nKey="theseIntegrationsAreStrongpreparedButNotYetLivestrongOnceYouEnterYourOwnAuthorisedApiCredentialsAndEnableThemTheConnectionArchitectureIsReadyToActivateNoMessagesOrCalendarEventsAreSentWithoutYourCredentials">These integrations are <strong>prepared but not yet live</strong>. Once you enter your own authorised API credentials and enable them, the connection architecture is ready to activate. No messages or calendar events are sent without your credentials.</Trans></span>
        </div>
      </div>

      {isAdmin && securityForm && (
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6" data-testid="security-alert-settings">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-sm bg-red-50 flex items-center justify-center">
                <ShieldCheck size={21} className="text-red-600" />
              </span>
              <div>
                <h3 className="font-serif text-xl">
                  {t("securityAlerts.title", "Security Login Alerts")}
                </h3>
                <p className="text-xs text-slate-500 max-w-2xl">
                  {t(
                    "securityAlerts.help",
                    "Monitor new devices, new IP addresses and repeated failed login attempts."
                  )}
                </p>
              </div>
            </div>

            <div className="flex gap-2">
              <Button
                variant="outline"
                className="rounded-sm"
                disabled={testSecurity.isPending || !securityForm.enabled}
                onClick={() => testSecurity.mutate()}
                data-testid="send-test-security-alert"
              >
                {t("securityAlerts.sendTest", "Send Test Alert")}
              </Button>

              <Button
                variant="outline"
                className="rounded-sm"
                disabled={testEmail.isPending || !securityForm.enabled}
                onClick={() => testEmail.mutate()}
                data-testid="send-test-email"
              >
                {t("securityAlerts.sendTestEmail", "Send Test Email")}
              </Button>

              <Button
                className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white"
                disabled={saveSecurity.isPending}
                onClick={() => saveSecurity.mutate(securityForm)}
                data-testid="save-security-alerts"
              >
                <FloppyDisk size={16} className="mr-1.5" />
                {t("securityAlerts.save", "Save Security")}
              </Button>
            </div>
          </div>

          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="border border-slate-100 rounded-sm p-4">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <Label>{t("securityAlerts.enable", "Enable Security Alerts")}</Label>
                  <p className="text-xs text-slate-500 mt-1">
                    {t("securityAlerts.enableHelp", "Turn login security monitoring and alert creation on or off.")}
                  </p>
                </div>
                <Switch
                  checked={!!securityForm.enabled}
                  onCheckedChange={(v) => setSecurityForm({ ...securityForm, enabled: v })}
                  data-testid="security-alert-enabled"
                />
              </div>
            </div>

            <div className="border border-slate-100 rounded-sm p-4">
              <Label>{t("securityAlerts.mobile", "Alert Mobile Number")}</Label>
              <Input
                className="mt-2 rounded-sm"
                placeholder="+351 ..."
                value={securityForm.phone || ""}
                onChange={(e) => setSecurityForm({ ...securityForm, phone: e.target.value })}
                data-testid="security-alert-phone"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t("securityAlerts.mobileHelp", "Use international format. The number can be changed at any time.")}
              </p>
            </div>

            <div className="border border-slate-100 rounded-sm p-4">
              <Label>{t("securityAlerts.alertEmail", "Alert Email Address")}</Label>
              <Input
                className="mt-2 rounded-sm"
                type="email"
                placeholder="alerts@panatlantik.com"
                value={securityForm.alert_email || ""}
                onChange={(e) => setSecurityForm({ ...securityForm, alert_email: e.target.value })}
                data-testid="security-alert-email"
              />
              <p className="text-xs text-slate-500 mt-1">
                {t("securityAlerts.alertEmailHelp", "Where security alert emails are sent. Leave blank to use the server default.")}
              </p>
            </div>

            <div className="border border-slate-100 rounded-sm p-4">
              <div className="text-sm font-semibold text-slate-700 mb-4">
                {t("securityAlerts.channels", "Delivery Channels")}
              </div>

              {[
                ["email_enabled", t("securityAlerts.email", "Email")],
                ["sms_enabled", t("securityAlerts.sms", "SMS")],
                ["whatsapp_enabled", t("securityAlerts.whatsapp", "WhatsApp")],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <Label>{label}</Label>
                  <Switch
                    checked={!!securityForm[key]}
                    onCheckedChange={(v) => setSecurityForm({ ...securityForm, [key]: v })}
                    data-testid={`security-${key}`}
                  />
                </div>
              ))}
            </div>

            <div className="border border-slate-100 rounded-sm p-4">
              <div className="text-sm font-semibold text-slate-700 mb-4">
                {t("securityAlerts.events", "Alert Events")}
              </div>

              {[
                ["alert_on_admin_login", t("securityAlerts.adminLogin", "Admin login")],
                ["alert_on_new_ip", t("securityAlerts.newIp", "New IP address")],
                ["alert_on_new_device", t("securityAlerts.newDevice", "New device / browser")],
                ["alert_on_failed_attempts", t("securityAlerts.failedAttempts", "Repeated failed logins")],
                ["alert_on_account_lockout", t("securityAlerts.accountLockout", "Account lockout")],
                ["alert_on_suspicious", t("securityAlerts.suspicious", "Suspicious activity")],
                ["alert_on_password_reset", t("securityAlerts.passwordReset", "Password reset requested")],
                ["alert_on_password_changed", t("securityAlerts.passwordChanged", "Password changed")],
              ].map(([key, label]) => (
                <div key={key} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                  <Label>{label}</Label>
                  <Switch
                    checked={!!securityForm[key]}
                    onCheckedChange={(v) => setSecurityForm({ ...securityForm, [key]: v })}
                    data-testid={`security-${key}`}
                  />
                </div>
              ))}
            </div>

            <div className="border border-slate-100 rounded-sm p-4 md:col-span-2">
              <Label>{t("securityAlerts.minimumRisk", "Minimum Risk Level")}</Label>
              <select
                value={securityForm.minimum_risk_level || "medium"}
                onChange={(e) =>
                  setSecurityForm({ ...securityForm, minimum_risk_level: e.target.value })
                }
                className="mt-2 h-10 w-full md:w-72 border border-slate-200 rounded-sm px-3 bg-white text-sm"
                data-testid="security-minimum-risk"
              >
                <option value="low">{t("securityAlerts.riskLow", "Low")}</option>
                <option value="medium">{t("securityAlerts.riskMedium", "Medium")}</option>
                <option value="high">{t("securityAlerts.riskHigh", "High")}</option>
                <option value="critical">{t("securityAlerts.riskCritical", "Critical")}</option>
              </select>
            </div>
          </div>

          <div className="mt-5 bg-amber-50 border border-amber-200 rounded-sm px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
            <Info size={18} className="flex-shrink-0 mt-0.5" />
            <span>
              {t(
                "securityAlerts.dryRunNotice",
                "Security alerts are currently operating in safe dry-run mode. SMS and WhatsApp providers are not yet connected, so no paid mobile messages are sent."
              )}
            </span>
          </div>

          <div className="mt-6 border-t border-slate-100 pt-5" data-testid="security-alert-recent">
            <div className="text-sm font-semibold text-slate-700 mb-3">
              {t("securityAlerts.recentTitle", "Recent Alert Deliveries")}
            </div>
            {(!recentAlerts?.items || recentAlerts.items.length === 0) ? (
              <p className="text-xs text-slate-500">{t("securityAlerts.recentEmpty", "No security alerts recorded yet.")}</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-slate-100">
                      <th className="py-2 pr-3 font-medium">{t("securityAlerts.colEvent", "Event")}</th>
                      <th className="py-2 pr-3 font-medium">{t("securityAlerts.colRisk", "Risk")}</th>
                      <th className="py-2 pr-3 font-medium">{t("securityAlerts.colStatus", "Status")}</th>
                      <th className="py-2 pr-3 font-medium">{t("securityAlerts.colTime", "Time")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentAlerts.items.map((item, i) => (
                      <tr key={i} className="border-b border-slate-50 last:border-0" data-testid="security-alert-recent-row">
                        <td className="py-2 pr-3">{t(`securityAlerts.event.${item.event_type}`, item.event_type)}</td>
                        <td className="py-2 pr-3">{t(`securityAlerts.risk${(item.risk_level || "").charAt(0).toUpperCase() + (item.risk_level || "").slice(1)}`, item.risk_level)}</td>
                        <td className="py-2 pr-3">{t(`securityAlerts.status.${item.email_status || item.status}`, item.email_status || item.status)}</td>
                        <td className="py-2 pr-3 text-slate-500">{item.created_at ? new Date(item.created_at).toLocaleString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {weights && (
        <div className="mt-6 bg-white border border-slate-200 rounded-sm p-6" data-testid="scoring-weights">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <span className="w-10 h-10 rounded-sm bg-[#C5A059]/15 flex items-center justify-center"><Sliders size={20} className="text-[#C5A059]" /></span>
              <div>
                <h3 className="font-serif text-xl">{t('scoring.weightsTitle', 'Scoring Weights')}</h3>
                <p className="text-xs text-slate-500 max-w-xl">{t('scoring.weightsHelp', 'Adjust how much each factor contributes to buyer/investor and opportunity scores.')}</p>
              </div>
            </div>
            {isAdmin && (
              <div className="flex gap-2">
                <Button variant="outline" className="rounded-sm" data-testid="reset-weights" onClick={() => setWeights(JSON.parse(JSON.stringify(scoring.defaults)))}>{t('scoring.resetDefaults', 'Reset to defaults')}</Button>
                <Button className="rounded-sm bg-[#0B2545] hover:bg-[#1a365d] text-white" data-testid="save-weights" onClick={() => saveWeights.mutate(weights)}>{t('scoring.saveWeights', 'Save weights')}</Button>
              </div>
            )}
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-5">
            {["contact", "opportunity"].map((grp) => (
              <div key={grp} className="border border-slate-100 rounded-sm p-4">
                <div className="text-sm font-semibold text-slate-700 mb-3">{grp === "contact" ? t('scoring.weightsContact', 'Buyer / Investor') : t('scoring.weightsOpportunity', 'Opportunity / Deal')}</div>
                <div className="space-y-3">
                  {Object.keys(weights[grp]).map((k) => (
                    <div key={k} className="flex items-center gap-3">
                      <Label className="flex-1 text-xs text-slate-600">{t(`scoring.weightKey.${k}`, k)}</Label>
                      <Input type="number" min="0" disabled={!isAdmin} data-testid={`weight-${grp}-${k}`} value={weights[grp][k]}
                        onChange={(e) => setWeights({ ...weights, [grp]: { ...weights[grp], [k]: e.target.value === "" ? 0 : Number(e.target.value) } })}
                        className="w-24 rounded-sm text-right" />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
